import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../api';

const TRIGGERABLE = {
  'Email Body Backfill': '/api/emails/backfill-trigger',
};

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`,
}));

function BackgroundProcessesPanel({ refreshKey }) {
  const [processes, setProcesses] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, 10000);
    return () => clearInterval(intervalRef.current);
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchData() {
    try {
      const [procRes, cfgRes] = await Promise.all([
        apiFetch('/api/background-processes'),
        apiFetch('/api/background-processes/config'),
      ]);
      if (procRes.ok) setProcesses(await procRes.json());
      if (cfgRes.ok) setConfig(await cfgRes.json());
    } catch {}
    finally { setLoading(false); }
  }

  async function saveConfig(updates) {
    const newConfig = { ...config, ...updates };
    setConfig(newConfig);
    try {
      await apiFetch('/api/background-processes/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newConfig),
      });
    } catch {}
  }

  function getProcessSetting(name) {
    return (config?.processes || {})[name] || { enabled: true, runAtHour: null };
  }

  function saveProcessSetting(name, updates) {
    const current = getProcessSetting(name);
    const processes = { ...(config?.processes || {}), [name]: { ...current, ...updates } };
    saveConfig({ processes });
  }

  function formatDuration(ms) {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
    return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
  }

  function timeAgo(iso) {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  }

  async function triggerProcess(name) {
    const url = TRIGGERABLE[name];
    if (!url) return;
    setTriggering(name);
    try {
      await apiFetch(url, { method: 'POST' });
      setTimeout(fetchData, 2000);
    } catch {}
    finally { setTriggering(null); }
  }

  if (loading) return <section className="card"><h2>Background Processes</h2><p>Loading...</p></section>;

  const selectStyle = { background: '#1a1b23', color: '#e4e4e7', border: '1px solid #3a3b45', borderRadius: 6, padding: '4px 8px', fontSize: 13 };

  return (
    <section className="card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Background Processes</h2>
        <button className="manage-btn" onClick={() => setShowConfig(!showConfig)} style={{ fontSize: 11 }}>
          {showConfig ? 'Hide Config' : 'Configure'}
        </button>
      </div>

      {/* Global config */}
      {showConfig && config && (
        <div style={{ marginBottom: 12, padding: 10, background: '#16171f', borderRadius: 8, border: '1px solid #2a2b35', fontSize: 13 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ color: '#a1a1aa' }}>Sync cycle:</span>
            <select value={config.syncIntervalMinutes} onChange={(e) => saveConfig({ syncIntervalMinutes: parseInt(e.target.value) })} style={selectStyle}>
              {[15, 30, 60, 120, 240, 480].map((m) => (
                <option key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60} hr`}</option>
              ))}
            </select>
            <span style={{ color: '#a1a1aa' }}>Backfill cycle:</span>
            <select value={config.backfillIntervalMinutes} onChange={(e) => saveConfig({ backfillIntervalMinutes: parseInt(e.target.value) })} style={selectStyle}>
              {[5, 10, 15, 30, 60, 120].map((m) => (
                <option key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60} hr`}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Process list */}
      {processes.length === 0 ? (
        <p style={{ color: '#71717a', fontSize: 13 }}>No background processes have run yet. They start after the app has been running for a few minutes.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {processes.map((p) => {
            const ps = getProcessSetting(p.name);
            const disabled = ps.enabled === false;
            return (
              <div key={p.name} style={{
                padding: '8px 0', borderBottom: '1px solid #22232d', fontSize: 13,
                opacity: disabled ? 0.4 : 1,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {showConfig ? (
                    <input type="checkbox" checked={ps.enabled !== false}
                      onChange={(e) => saveProcessSetting(p.name, { enabled: e.target.checked })}
                      style={{ flexShrink: 0 }} />
                  ) : (
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      background: disabled ? '#52525b' : p.isRunning ? '#22c55e' : p.lastError ? '#f87171' : '#3b82f6',
                      boxShadow: p.isRunning && !disabled ? '0 0 6px #22c55e' : 'none',
                    }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#e4e4e7' }}>{p.name}</div>
                    {!showConfig && p.lastSummary && <div style={{ fontSize: 11, color: '#71717a', marginTop: 1 }}>{p.lastSummary}</div>}
                    {!showConfig && p.lastError && <div style={{ fontSize: 11, color: '#f87171', marginTop: 1 }}>{p.lastError}</div>}
                    {showConfig && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                        <span style={{ fontSize: 11, color: '#71717a' }}>Run at:</span>
                        <select value={ps.runAtHour ?? ''} onChange={(e) => saveProcessSetting(p.name, { runAtHour: e.target.value === '' ? null : parseInt(e.target.value) })} style={{ ...selectStyle, fontSize: 11, padding: '2px 4px' }}>
                          <option value="">Every cycle</option>
                          {HOURS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ color: '#a1a1aa', fontSize: 12 }}>{timeAgo(p.lastCompleted)}</div>
                    <div style={{ color: '#52525b', fontSize: 11 }}>{formatDuration(p.durationMs)}</div>
                  </div>
                  <div style={{ textAlign: 'center', flexShrink: 0, minWidth: 40 }}>
                    <div style={{ color: '#a1a1aa', fontSize: 14, fontWeight: 700 }}>{p.runCount}</div>
                    <div style={{ color: '#52525b', fontSize: 10 }}>runs</div>
                  </div>
                  {TRIGGERABLE[p.name] && (
                    <button className="manage-btn" onClick={() => triggerProcess(p.name)}
                      disabled={triggering === p.name || p.isRunning}
                      style={{ fontSize: 11, padding: '3px 8px', flexShrink: 0 }}>
                      {triggering === p.name ? 'Triggering...' : p.isRunning ? 'Running' : 'Run Now'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default BackgroundProcessesPanel;
